// angular
import {Component, OnInit, effect, inject} from "@angular/core";
import {
  FormGroup,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {Router, ActivatedRoute} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {TranslateModule} from "@ngx-translate/core";
// material angular
import {MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatTabsModule} from "@angular/material/tabs";
import {MatButtonModule} from "@angular/material/button";
// components
import {FiltersDialogFrameComponent} from "@app/core/components/filters-dialog-frame/filters-dialog-frame.component";
import {SelectComponent} from "@app/core/components/select/select.component";
import {DatePickerComponent} from "@app/core/components/date-picker/date-picker.component";
import {InputComponent} from "@app/core/components/input/input.component";
// services
import {CustomerService} from "@app/+customer/shared/services/customer.service";
import {UserService} from "@app/shared/services/user.service";
// types
import {SelectItemS} from "src/app/core/components/select/Item.type";
import {Seller} from "src/app/shared/types/seller.type";
import {customerTypeEnum} from "@app/+customer/shared/types/customer-type.type";
import {customerCategoryEnum} from "@app/+customer/shared/types/customer-category.type";
import {CustomerCredibilityEnum as CredibilityEnum} from "@app/+customer/shared/types/customer-credibility.type";
import {ICustomer} from "@app/+customer/shared/types/customer.type";
import {ISaleLine} from "@app/+capillary/shared/types/line.type";
import {IUserData} from "@app/shared/types/employee.type";
import {Role} from "@app/shared/types/role.enum";
// utils
import {removeFormNullValues} from "src/app/shared/utils/removeFormNullValues";
import convertDateToISO from "src/app/shared/utils/convertDateToISO";
import {generateSelectOptionsFromEnum} from "src/app/shared/utils/generateSelectOptionsFromEnum";
import {SharedModule} from "@app/shared/shared.module";
// constants
import {
  didWeContactOptions,
  lockedStatusOptions,
  propertyOwnershipStatusOptions,
} from "./customer-list-filters-dialog-select-options";
import {formGridStyle} from "src/app/shared/constants/css-classes";
// directives
import {OnlyNumberDirective} from "@app/core/directives/only-number.directive";
import {CommasSeparateNumbersDirective} from "@app/core/directives/commas-separate-numbers.directive";
import {PreventFormSubmitDirective} from "@app/core/directives/form-enter-handler.directive";

@Component({
  selector: "app-customer-list-filters-dialog",
  templateUrl: "./customer-list-filters-dialog.component.html",
  styleUrls: ["./customer-list-filters-dialog.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FiltersDialogFrameComponent,
    SelectComponent,
    MatIconModule,
    SharedModule,
    MatTabsModule,
    MatButtonModule,
    DatePickerComponent,
    InputComponent,
    OnlyNumberDirective,
    CommasSeparateNumbersDirective,
    PreventFormSubmitDirective,
  ],
})
export class CustomerListFiltersDialogComponent implements OnInit {
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _spinner = inject(NgxSpinnerService);
  private _customerService = inject(CustomerService);
  private _userService = inject(UserService);
  dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef);
  private _qParams: Record<string, string>;

  public Roles = Role;
  public user: IUserData;
  public userRoles: Role[];
  public formGridStyle = formGridStyle;
  public form: FormGroup;
  public sellersOptions: SelectItemS = [];
  public representativesOptions: SelectItemS = [];
  public saleLineOptions: SelectItemS = [];
  public customersOptions: SelectItemS = [];
  public propertyOwnershipStatusOptions = propertyOwnershipStatusOptions;
  public didWeContactOptions = didWeContactOptions;
  public lockedStatusOptions = lockedStatusOptions;

  public categoryOptions = generateSelectOptionsFromEnum(customerCategoryEnum, {
    renderRemoveOption: true,
  });
  public credibilityOptions = generateSelectOptionsFromEnum(CredibilityEnum, {
    renderRemoveOption: true,
  });
  public typeOptions = generateSelectOptionsFromEnum(customerTypeEnum, {
    renderRemoveOption: true,
  });

  constructor() {
    effect(() => {
      this.userRoles = this._userService.roles();
    });
  }

  ngOnInit() {
    this._userService.user$.subscribe((user) => (this.user = user));

    // get sellers data
    this._customerService.getSellers().subscribe((res) => {
      this._generateSellerOptions(res);
    });
    // get sale lines data
    this._customerService.getSaleLines().subscribe((res) => {
      this._generateSaleLineOptions(res.data);
    });

    this.form = this._getFormInitialValue();
    this._activatedRoute.queryParams.subscribe((qParams) => {
      this._qParams = qParams;
    });
    // get customers
    this._getCustomers();
    this._patchFormFieldsValues();
  }

  private _getFormInitialValue() {
    return new FormGroup({
      created_at_min: new FormControl(null),
      created_at_max: new FormControl(null),
      title: new FormControl(null),
      person_id: new FormControl(null),
      code: new FormControl(null),
      phone: new FormControl(null),
      mobile: new FormControl(null),
      hp_title: new FormControl(null),
      hp_code: new FormControl(null),
      seller_id: new FormControl(""),
      category: new FormControl(""),
      type: new FormControl(""),
      credibility_by_seller: new FormControl(""),
      credibility_by_sales_manager: new FormControl(""),
      capillary_sales_line_id: new FormControl(""),
      min_order_count: new FormControl(null),
      is_property_owner: new FormControl(""),
      did_we_contact: new FormControl(""),
      locked: new FormControl(""),
    });
  }

  private _generateSellerOptions(res: Seller[]) {
    this.sellersOptions = [{value: "", key: "all"}].concat(
      res.map((item) => ({
        value: item?.id,
        key: item.profile?.first_name?.concat(` ${item?.profile?.last_name}`),
      }))
    );
  }

  private _generateSaleLineOptions(res: ISaleLine[]) {
    this.saleLineOptions = [{value: "", key: "همه"}].concat(
      res.map((item) => ({
        value: item.id,
        key: item.line_number + " - " + item.title,
      }))
    );
  }

  private _patchFormFieldsValues() {
    if (Object.keys(this._qParams).length) {
      setTimeout(() => {
        this.form.patchValue({
          ...this._qParams,
          created_at_min: this._qParams["created_at_min"]
            ? this._qParams["created_at_min"]
            : null,
          created_at_max: this._qParams["created_at_max"]
            ? this._qParams["created_at_max"]
            : null,
          min_order_count: Number(this._qParams["min_order_count"]) || null,
        });
      }, 100);
    }
  }

  public reset() {
    this.form.reset(this._getFormInitialValue().value);
    this.submit();
  }

  public submit() {
    const createdMin = this.form.value.created_at_min;
    const createdMax = this.form.value.created_at_max;

    const values = {
      ...this.form.value,
      min_order_count: this.form.value.min_order_count,
      created_at_min: createdMin ? convertDateToISO(createdMin) : null,
      created_at_max: createdMax ? convertDateToISO(createdMax) : null,
    };

    const sort = this._qParams?.["sort"];
    const queryParams = removeFormNullValues({
      ...values,
      ...(sort ? {sort} : {}),
    });
    if (JSON.stringify(this._qParams) === JSON.stringify(queryParams)) {
      this.dialogRef.close();
      return;
    }
    this._spinner.show();
    this._router.navigate([], {queryParams}).then(() => {
      this.dialogRef.close();
    });
  }

  private _generateRepresentativeOptions(representatives: ICustomer[]) {
    this.representativesOptions = [{value: "", key: "همه"}].concat(
      representatives
        .map((c) => {
          return c.people.map((user) => {
            return {
              key: `${user.profile?.first_name || ""}  ${user.profile?.last_name || ""}`,
              value: user?.id,
            };
          });
        })
        .flat()
    );
  }

  private _getCustomers() {
    this._customerService.getAllCustomers().subscribe((res) => {
      this._generateRepresentativeOptions(res.data);
    });
  }
}
